import type { Mutation, Query } from '@tanstack/react-query';
import { api } from '../api';

let isRefreshing = false;
let isRedirecting = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: Array<{
  query?: Query<unknown, unknown, unknown, ReadonlyArray<unknown>>;
  mutation?: Mutation<unknown, unknown, unknown, unknown>;
  variables?: unknown;
  resolve?: () => void;
}> = [];

const refreshToken = async (): Promise<string> => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Start refresh process
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await api
        .post('auth/refresh', {
          json: { refresh_token: refreshTokenValue },
        })
        .json<{ data: { access_token: string; refresh_token?: string } }>();

      const newAccessToken = response.data.access_token;
      localStorage.setItem('access_token', newAccessToken);

      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }

      return newAccessToken;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const clearTokensAndRedirect = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  failedQueue = [];
  isRefreshing = false;

  if (!isRedirecting) {
    isRedirecting = true;
    window.location.href = '/login';
  }
};

const processFailedQueue = () => {
  failedQueue.forEach(({ query, mutation, variables, resolve }) => {
    if (mutation) {
      const { options } = mutation;
      mutation.setOptions({ ...options });
      mutation.execute(variables);
    }
    if (query) {
      query.fetch();
    }
    if (resolve) {
      resolve();
    }
  });
  failedQueue = [];
};

export const refreshTokenAndRetry = async (
  query?: Query<unknown, unknown, unknown, ReadonlyArray<unknown>>,
  mutation?: Mutation<unknown, unknown, unknown, unknown>,
  variables?: unknown,
): Promise<void> => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (!refreshTokenValue) {
    clearTokensAndRedirect();
    return;
  }

  // Return a promise that resolves after token refresh
  // This maintains loading state
  return new Promise<void>((resolve) => {
    const queueItem = { query, mutation, variables, resolve };

    if (isRefreshing) {
      failedQueue.push(queueItem);
      return;
    }

    isRefreshing = true;
    failedQueue.push(queueItem);

    (async () => {
      try {
        await refreshToken();

        processFailedQueue();
        resolve();
      } catch {
        clearTokensAndRedirect();
        resolve();
      }
    })();
  });
};

export const queryErrorHandler = (
  error: Error,
  query: Query<unknown, unknown, unknown, ReadonlyArray<unknown>>,
): void => {
  const httpError = error as { response?: { status?: number } };
  if (httpError.response?.status === 401) {
    refreshTokenAndRetry(query);
  }
};

export const mutationErrorHandler = (
  error: Error,
  variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
): void => {
  const httpError = error as { response?: { status?: number } };
  if (httpError.response?.status === 401) {
    refreshTokenAndRetry(undefined, mutation, variables);
  }
};
